import { getActivePinia } from 'pinia';
import type { ApiParams } from './types';
import { useGlobalAbortController } from '@Composables';
import { API_URL, HTTP_HEADER_AF_CART_ID, HTTP_HEADER_AF_CLIENT_URL } from '@Constants';
import { axios } from '@Libraries';

const CART_STORE_ID = 'shoppingCart';

const DEFAULT_AXIOS_OPTIONS = {
  validateStatus: (status: number) => status >= 200 && status < 300,
};

export default async function (params: ApiParams) {
  const { endpoint, method, payload, isUpload, headers: extraHeaders, responseType, timeout } = params;
  const apiEndpoint = endpoint.replace('/api', API_URL);

  const { getCurrentController } = useGlobalAbortController();
  const currentController = getCurrentController();

  const staffAppUrlHeader =
    typeof window !== 'undefined' && window.location?.href ? { [HTTP_HEADER_AF_CLIENT_URL]: window.location.href } : {};

  // NOTE :: Tells the API which checkout this tab thinks it is editing, so it can refuse a request
  // aimed at a cart the session no longer holds. Spread in only when there is one — a request with
  // no cart id falls back to the API's "is the cart real?" guard.
  //
  // Read straight off the cart store's state instead of keeping a synced copy. This file cannot
  // import the store (the store's services import `@Providers`, which closes the cycle), but `pinia`
  // is a node module, so reaching the state through the active instance is free of that problem —
  // and there is no second copy that can drift. Absent until the store is first used, which is
  // correct: no cart open, no header.
  const cartData = getActivePinia()?.state.value?.[CART_STORE_ID]?.cartData;
  const activeCartId = cartData?.cart_id?.$oid || cartData?.cart_id || '';
  const cartIdHeader = activeCartId ? { [HTTP_HEADER_AF_CART_ID]: activeCartId } : {};

  const axiosOptions = {
    ...DEFAULT_AXIOS_OPTIONS,
    method,
    data: payload,
    signal: currentController?.signal,
    // NOTE :: Only set when the caller asks for it, so the 30s axios default still applies
    // everywhere else.
    ...(timeout ? { timeout } : {}),
    ...(responseType ? { responseType } : {}),
    headers: {
      'Content-Type': isUpload ? 'multipart/form-data' : 'application/json',
      ...cartIdHeader,
      ...staffAppUrlHeader,
      ...extraHeaders,
    },
  };

  return axios(apiEndpoint, { ...axiosOptions }).then((response) => response?.data);
}
